/*
 * Copyright (c) 2020. American Express Company
 */

import * as _ from 'lodash';
import { LocalDate } from '@js-joda/core';
import React, { Component, ReactComponentElement } from 'react';

// models and services
import { AccountModel } from 'src/types/account.model';
import { CardModel } from 'src/types/card.model';
import FormatService from '../../services/format.service';
import LiteralsService from '../../services/literals/LiteralsService';
import NavigationService from '../../services/navigation.service';
import Typography from '../typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// components
import BWRadioGroup from '../bw-radio-group/BWRadioGroup';
import Dropdown from '../menus/Dropdown';
import { FieldControl, Label } from '@americanexpress/dls-react';
import GoalDrawer from './GoalDrawer';
import GoalProgressBar from '../goal-progress-bar/GoalProgressBar';
import InfoCallout from '../callout/InfoCallout';
import Output from '../atomic/molecules/output/Output';
import SavingsPerMonth from '../savings-per-month/SavingsPerMonth';
import StatusCallout from '../callout/StatusCallout';

// icons and styles
import { ReactComponent as PigBankIcon } from 'src/bw/goal/piggy-bank.svg';
import HighYieldSavingsCallout from 'src/components/callout/HighYieldSavingsCallout';

export default class EmergencyFundDrawer extends Component<
    any,
    {
        initialRender: boolean;
    }
> {
    public override state = {
        initialRender: true
    };

    public override render(): ReactComponentElement<any> {
        const drawerProps: any = this.props.drawerProps;
        const card: CardModel = drawerProps.card;
        const accounts: AccountModel[] = _.get(this.props, 'user.accounts');
        const savingsAccounts = _.filter(accounts, (account: AccountModel) => account.subtype === 'savings');
        let emergencyFundExpenses: number = Math.max(_.get(this, 'props.user.attributes.emergencyFundExpenses', 0), 0);
        if (isNaN(emergencyFundExpenses)) {
            emergencyFundExpenses = Number(
                _.get(this, 'props.user.attributes.emergencyFundExpenses').replace(/[^\d.-]/g, '')
            );
        }

        const months = Number(card.attributes.months) || 3;
        emergencyFundExpenses = (emergencyFundExpenses / 3) * months;

        let current = 0;
        if (card.accountId) {
            const account = _.find(savingsAccounts, { id: card.accountId });
            current = account.current;
        }

        const delta = Math.max(0, emergencyFundExpenses - current);
        const surplus: number = Math.max(this.getMonthlyAvailable(), 0);

        let monthsLeft = -1;
        if (card.attributes.perMonth) {
            monthsLeft = Math.ceil(delta / card.attributes.perMonth);
            card.endDate = card.startDate.plusMonths(monthsLeft);
        }
        card.attributes.targetBalance = emergencyFundExpenses;

        const percentSaved = current ? Math.min(100, Math.round((100 * current) / emergencyFundExpenses)) : 0;

        let status: JSX.Element = null;
        if (delta <= 0) {
            status = <StatusCallout status="success" icon="check" text="You've met your goal." />;
        } else {
            if (monthsLeft !== -1) {
                if (card.attributes.perMonth > surplus) {
                    status = (
                        <StatusCallout
                            status="error"
                            icon="warning"
                            text="You can't comfortably save this much every month."
                        />
                    );
                } else {
                    status = (
                        <StatusCallout
                            status="success"
                            icon="check"
                            text={`Based on your per-month savings, you should meet your savings goal
                                              in ${FormatService.formatDateLongMonth(card.endDate)}`}
                        />
                    );
                }
            }
        }

        return (
            <GoalDrawer
                drawerProps={drawerProps}
                handleBack={this.props.handleBack}
                handleUpdateCard={this.props.handleUpdateCard}
                handleChangeCard={this.props.handleChangeCard}
                handleDeleteCard={this.props.handleDeleteCard}
                showLoader={this.props.showLoader}
                hideLoader={this.props.hideLoader}
                icon={<PigBankIcon />}
                introContent={LiteralsService.getLiteral('EmergencyFundDrawer.intro')}
                cashFlow={this.props.user.cashFlow}
                handleChangeCardDate={this.props.handleChangeCardDate.bind(this)}
                startDateLabel="When do you want to start saving?"
                updateAction={() => {
                    card.attributes.started = 'true';
                    card.attributes.targetBalance = emergencyFundExpenses;
                }}
                afterUpdateAction={this.afterUpdateAction.bind(this)}
            >
                {card.created !== card.updated && (
                    <GoalProgressBar
                        currentAmount={Math.min(emergencyFundExpenses, current)}
                        percent={percentSaved}
                        targetAmount={emergencyFundExpenses}
                    />
                )}

                <Output
                    title={LiteralsService.getLiteral('EmergencyFundDrawer.suggest')}
                    main={FormatService.format(emergencyFundExpenses)}
                />

                <BWRadioGroup
                    label="How many months do you want to save for?"
                    radioOptions={[
                        { id: 'radio-3', label: '3 months', value: '3' },
                        { id: 'radio-6', label: '6 months', value: '6' },
                        { id: 'radio-12', label: '12 months', value: '12' }
                    ]}
                    onChange={(e: any) => {
                        this.changeMonths(e.target.value);
                    }}
                    defaultChecked={months.toString()}
                />

                <div className="drawerSection">
                    <FieldControl>
                        <Label htmlFor="emergencyFundAccountPicker">What account do you want to save to?</Label>
                        <Dropdown
                            id="emergencyFundAccountPicker"
                            value={card.accountId ? card.accountId : undefined}
                            className="after4"
                            onChange={(e: any) => {
                                this.props.handleChangeCard(drawerProps.card, e.target.value, 'accountId');
                            }}
                            fullWidth={true}
                        >
                            <option key="account_none" />
                            {savingsAccounts.map((account: any) => (
                                <option key={account.id} value={account.id}>
                                    {account.name}
                                </option>
                            ))}
                        </Dropdown>
                    </FieldControl>
                    {card.accountId && (
                        <InfoCallout noMargin>Current balance: {FormatService.format(current)}</InfoCallout>
                    )}
                </div>

                {!savingsAccounts.length && (
                    <InfoCallout noMargin>
                        <div className="rows">
                            <Typography variant={TypographyVariants.CAPTION} className="text" key="noaccount-title">
                                {LiteralsService.getLiteral('EmergencyFundDrawer.connected')}
                            </Typography>
                            <Typography
                                variant={TypographyVariants.CAPTION}
                                className="subtext link"
                                key="noaccount-link"
                                onClick={() => {
                                    NavigationService.goTo(
                                        this.props.drawerProps.history,
                                        '/drawer/add-accounts',
                                        'Add accounts'
                                    ).catch(console.error);
                                }}
                            >
                                {LiteralsService.getLiteral('EmergencyFundDrawer.enable')}
                            </Typography>
                        </div>
                    </InfoCallout>
                )}

                <HighYieldSavingsCallout />

                <SavingsPerMonth
                    delta={delta}
                    surplus={surplus}
                    inputLabel="Monthly savings"
                    inputValue={card.attributes.perMonth}
                    inputOnChange={(value: number) => {
                        card.attributes.perMonth = value;
                        this.props.handleChangeCardAttribute(
                            card,
                            value ? Math.abs(value) : null,
                            'perMonth',
                            'number'
                        );
                    }}
                    status={status}
                />
            </GoalDrawer>
        );
    }

    private changeMonths(value: string): void {
        this.props.handleChangeCardAttribute(this.props.drawerProps.card, value, 'months', 'currency');
    }

    /** Get the monthly available to save from the extended/edit card payload */
    private getMonthlyAvailable(): number {
        const card: CardModel = this.props.drawerProps.card;
        const startDate: LocalDate = card.startDate || LocalDate.now();
        return Math.floor(_.get(card, 'monthlyNetIncome.' + startDate.toJSON(), 0));
    }

    private async afterUpdateAction(): Promise<void> {
        this.props.handleBack();
    }
}
